import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowsClockwise, Eye, Sparkle, Path, FileText } from "@phosphor-icons/react";
import { ConsultButton } from "@/components/ConsultCTA";
import api from "@/lib/api";

export default function Reports({ onNavigate }) {
  const [primaries, setPrimaries] = useState([]);
  const [cross, setCross] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    Promise.all([
      api.get("/profile").then((r) => setPrimaries(r.data.primaries || [])),
      api.get("/reports/cross-pattern").then((r) => setCross(r.data)).catch(() => {}),
    ]).finally(() => setLoading(false));
  }, []);

  if (loading) return <div className="flex justify-center py-32"><div className="h-9 w-9 animate-spin rounded-full border-2 border-slate-800 border-t-teal-400" /></div>;

  return (
    <div>
      <p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">My Reports</p>
      <h1 className="mt-2 font-heading text-3xl font-light tracking-tight text-slate-50">Your results library</h1>

      {primaries.length === 0 ? (
        <div className="mt-8 rounded-2xl border border-dashed border-slate-800 p-12 text-center" data-testid="reports-empty">
          <FileText size={32} weight="duotone" className="mx-auto text-slate-600" />
          <h2 className="mt-4 font-heading text-xl font-medium text-slate-100">No reports yet</h2>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-400">Take an assessment to start building your library of insights.</p>
          <button onClick={() => onNavigate("assessments")} className="mt-5 inline-flex items-center gap-2 rounded-full bg-teal-500 px-6 py-3 text-sm font-semibold text-slate-950 hover:bg-teal-400">Browse assessments</button>
        </div>
      ) : (
        <>
          {/* Cross-pattern synthesis */}
          {cross?.summary && (
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} className="mt-8 rounded-2xl border border-teal-500/30 bg-gradient-to-br from-slate-900 to-slate-950 p-7" data-testid="cross-pattern">
              <div className="flex items-center gap-2"><Sparkle size={16} weight="fill" className="text-teal-400" /><p className="text-xs font-bold uppercase tracking-[0.2em] text-teal-500">Cross-Pattern Insight</p></div>
              <h3 className="mt-3 font-heading text-xl font-medium text-slate-100">The thread connecting your results</h3>
              <ul className="mt-3 space-y-2">{cross.summary.map((s, i) => <li key={i} className="text-sm leading-relaxed text-slate-300">{s}</li>)}</ul>
              <div className="mt-4 flex flex-wrap gap-2">{cross.patterns?.map((p, i) => <span key={i} className="rounded-full border border-slate-700 bg-slate-950 px-3 py-1 text-xs text-slate-400">{p}</span>)}</div>
            </motion.div>
          )}

          <div className="mt-6 grid gap-4 md:grid-cols-2">
            {primaries.map((p) => (
              <motion.div key={p.result_id} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} className="rounded-2xl border border-slate-800 bg-slate-900 p-6" data-testid={`report-card-${p.slug}`}>
                <div className="flex items-center gap-2"><span className="h-2.5 w-2.5 rounded-full" style={{ background: p.primary_color }} /><p className="text-xs uppercase tracking-wider text-slate-500">{p.assessment_name}</p></div>
                <h3 className="mt-2 font-heading text-2xl font-light" style={{ color: p.primary_color }}>{p.primary_name}</h3>
                <p className="mt-2 text-sm leading-relaxed text-slate-400">{p.insight}</p>
                <p className="mt-3 text-xs text-slate-600">{new Date(p.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}</p>
                <div className="mt-4 flex flex-wrap items-center gap-2">
                  <Link to={`/results/${p.result_id}`} className="inline-flex items-center gap-1.5 rounded-full bg-teal-500 px-4 py-2 text-xs font-semibold text-slate-950 hover:bg-teal-400" data-testid={`report-view-${p.slug}`}><Eye size={14} weight="bold" /> View</Link>
                  <Link to={`/assessment/${p.slug}`} className="inline-flex items-center gap-1.5 rounded-full border border-slate-700 px-4 py-2 text-xs font-medium text-slate-200 hover:border-slate-500" data-testid={`report-retake-${p.slug}`}><ArrowsClockwise size={14} /> Retake</Link>
                  <ConsultButton source={`report-${p.slug}`} />
                </div>
              </motion.div>
            ))}
          </div>

          <div className="mt-6 flex items-center justify-between rounded-2xl border border-slate-800 bg-slate-900 p-6">
            <div className="flex items-center gap-3"><span className="flex h-10 w-10 items-center justify-center rounded-lg bg-slate-800 text-teal-400"><Path size={20} weight="duotone" /></span><div><h3 className="font-heading text-base font-medium text-slate-100">Map more of yourself</h3><p className="text-sm text-slate-500">Each assessment deepens your profile.</p></div></div>
            <button onClick={() => onNavigate("assessments")} className="inline-flex items-center gap-2 rounded-full border border-slate-700 px-5 py-2.5 text-sm font-medium text-slate-200 hover:border-slate-500">Browse</button>
          </div>
        </>
      )}
    </div>
  );
}
